import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  color,
  type BoundingBox,
  type BoundingBoxStyle,
  type ViewerPolygon,
  type PolygonEditorApi,
} from "@aeriosiq/ui";
import { AnalysisViewerPanel } from "./viewer/AnalysisViewerPanel.js";
import type { AiFinding, ImageRef } from "@aeriosiq/types";
import { useMe } from "@aeriosiq/auth-client";
import {
  useDataAcquisitionDetailQuery,
  useDataPointsQuery,
} from "./queries.js";
import { useDataPointFilterStore } from "./state/dataPointFilterStore.js";
// ANA-R1-02 — the single effective AI-finding set shared by List/Tiles/Viewer
// so all three agree on which findings exist on the current image.
import { useEffectiveFindingSet } from "./useEffectiveFindingSet.js";
// ANA-R1-03 — the AI-findings tab badge count, scoped to the current global
// data-point filter + the AI class/confidence narrowing (shared selectors).
import { useFilteredFindingCount } from "./useFilteredFindingCount.js";
// ANA-R1-04 — shared raw-class-name → model display-name lookup for the Viewer
// bbox labels (canonical display name everywhere user-facing).
import { useClassDisplayNames } from "./useClassDisplayNames.js";
import { useFindingSelectionReconciler } from "./useFindingSelectionReconciler.js";
// ANA-R1-05 — on dataset-open / DA change, mark the AI-finding + facet caches
// stale so a reopened dataset always refetches post-(re)import finding ids
// (defeats the benign stale-cache "class flip").
import { useInvalidateFindingsOnOpen } from "./useInvalidateFindingsOnOpen.js";
import { AnalysisStatusbar } from "./shell/AnalysisStatusbar.js";
import {
  useAnalysisReadinessBridge,
  type QueryLike,
} from "./shell/useAnalysisReadinessBridge.js";
import { type ReadinessStep } from "./state/analysisReadinessStore.js";
import { useImagesQuery } from "./queries/useImagesQuery.js";
import { useImageActivationStore } from "./state/imageActivationStore.js";
import { useWorkspaceUiStore } from "./state/workspaceUiStore.js";
import { useSelectionStore } from "./state/selectionStore.js";
import { useEscapeLayer } from "./state/useEscapeLayer.js";
import { ESCAPE_PRIORITY, registerEscapeLayer } from "./state/escapeStack.js";
import { useViewerZoomStore } from "./state/viewerZoomStore.js";
import { useUndoStackStore } from "./state/undoStackStore.js";
import { useUndoableSoftDeleteDefectMutation } from "../defects/queries.js";
import { useUndoHotkey } from "./useUndoHotkey.js";
import { useDataPointNavigation } from "./useDataPointNavigation.js";
// TRIAGE-01/02 (audit 3.2) — global triage hotkey layer + decisions + prefs.
import { useTriageHotkeys } from "./triage/useTriageHotkeys.js";
import { useTriageDecisions } from "./triage/useTriageDecisions.js";
import { useTriagePreferencesStore } from "./triage/triagePreferencesStore.js";
import { AnalysisHeader, AnalysisHeaderSkeleton } from "./AnalysisHeader.js";
import { AnalysisWorkspace, type WidgetKey } from "./AnalysisWorkspace.js";
import { FilterWidget } from "./FilterWidget.js";
import { FilmstripWidget } from "./shell/widgets/FilmstripWidget.js";
import { TilesWidget } from "./shell/widgets/TilesWidget.js";
import { ListWidget } from "./shell/widgets/ListWidget.js";
import { SketchWidget } from "./shell/widgets/SketchWidget.js";
import { AiFilterWidget } from "./shell/widgets/AiFilterWidget.js";
import { DefectFilterWidget } from "./shell/widgets/DefectFilterWidget.js";
import { HistogramWidget } from "./shell/widgets/HistogramWidget.js";
import { DrawOverlay } from "./components/DrawOverlay.js";
import { EditGeometryOverlay } from "./components/EditGeometryOverlay.js";
import {
  EditGeometryToolbar,
  type EditableDefect,
} from "./components/EditGeometryToolbar.js";
import { useEditGeometryStore } from "./state/editGeometryStore.js";
import { useViewportApiStore } from "./state/viewportApiStore.js";
import { DefectDrawer } from "./components/DefectDrawer.js";
import {
  useAssetsByIds,
  useSitesByIds,
  indexById,
  formatAsset,
  formatSite,
} from "../data-acquisitions/enrichment.js";
import { useFindingsQuery } from "../findings/index.js";
import {
  imageScopedDefectsParams,
  useDefectsQuery,
} from "../defects/queries.js";
import {
  resolveDefectEditPermission,
  type DefectEditPermission,
} from "./editPermission.js";
import { CataloguesBootstrap } from "../catalogues/index.js";
import { useEffectiveClassModel } from "../catalogues/useEffectiveClassModel.js";
import {
  PreferencesBootstrap,
  useLysePreferencesQuery,
  type BboxKindStyle,
} from "../preferences/index.js";
import { SettingsModal } from "./settings/SettingsModal.js";
import {
  useBBoxToggles,
  useBBoxTogglesUrlSync,
} from "./viewer/useBBoxToggles.js";
import { ViewerToolbar } from "./viewer/ViewerToolbar.js";
import { mountImageStateFilterBridge } from "./state/imageStateFilterStore.js";
import { useFocusEqualsReviewEffect } from "./useFocusEqualsReviewEffect.js";
import { SplitViewOverlay } from "./split-view/SplitViewOverlay.js";
import { useDeleteWithCascadeBridge } from "./useDeleteWithCascadeBridge.js";
import { useImageReviewState } from "./useImageReviewState.js";
import { useCachedImageUrl } from "./cache/useCachedImageUrl.js";
import { PrefetchProvider } from "./cache/PrefetchProvider.js";
import { DatasetDownloadPrompt } from "./cache/DatasetDownloadPrompt.js";
import { installCacheTestBridge } from "./cache/cacheTestBridge.js";
import {
  LockContentionDialog,
  ReadOnlyCollaboratorBanner,
  useLockEffect,
  useLockStore,
} from "./lock/index.js";
import {
  PopoutInfraMount,
  PopoutHost,
  readPopoutParams,
  useViewerPopoutSync,
  useWorkspaceSync,
  useFilterSync,
} from "./multiwindow/index.js";
import type {
  AssetSummary,
  DataAcquisitionDetail as DataAcquisitionDetailType,
  SiteSummary,
} from "@aeriosiq/types";
import { InboxIcon } from "@aeriosiq/ui/icons";

// SETTINGS-02 — map a per-kind bbox preference into the Viewer's
// resolved per-box style shape. The Viewer falls back to legacy
// constants for any omitted field, so this is a 1:1 projection.
function toBoxStyle(k: BboxKindStyle): BoundingBoxStyle {
  return {
    strokeWidth: k.lineWidth,
    activeStrokeWidth: k.lineWidthActive,
    color: k.color,
    activeColor: k.colorActive,
    fillOpacity: k.fillOpacity,
    activeFillOpacity: k.fillOpacityActive,
    outline: k.outline,
    label: {
      show: k.label.show,
      selectedOnly: k.label.when === "selectedOnly",
      fontSize: k.label.fontSize,
      textColor: k.label.textColor,
      bgColor: k.label.bgColor,
      bgOpacity: k.label.bgOpacity,
      position: k.label.position,
    },
  };
}

// SETTINGS-02 / OQ-SB6-C3 — fallback defect-severity palette used when
// `colorBy: severity` is on but the row has no `classColor`. When the active
// class model is NONE (no grades), defects fall through to the user's
// configured defect colour.
//
// D83's `severity` ramp is now the app's ONLY severity ramp. This table and
// the SketchWidget's used to be two different five-step scales that disagreed
// about what a grade looks like — this one an Apple-HIG mix, that one Material
// Design — so the same grade 2 defect was chartreuse in the sketch and
// yellow-green in the overlay. Both now index the same token set, and the
// grade→slot mapping is `gradeToSeverity`'s absolute branch (1 → info … 5 →
// critical, `defects/severityDisplay.ts`), which is the gateway's own
// `classCategoryToSeverity`. Note there is no GREEN step any more: D83 reuses
// D81's slots, and green means `success`, which is not a severity.
//
// Read from `color.dark` because these boxes are drawn over the photograph on
// the Analysis canvas — a calibrated near-black in both themes, exactly as the
// bounding-box preference defaults are.
const SEVERITY_BBOX_FALLBACK: Record<number, string> = {
  1: color.dark.severity.info,
  2: color.dark.severity.low,
  3: color.dark.severity.medium,
  4: color.dark.severity.high,
  5: color.dark.severity.critical,
};

function NotFoundPanel() {
  return (
    <Paper
      variant="outlined"
      role="region"
      aria-label="Inspection not found"
      sx={{
        p: 6,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        textAlign: "center",
      }}
    >
      <InboxIcon sx={{ fontSize: 48, color: "text.secondary" }} aria-hidden />
      <Typography variant="h6" component="p">
        Inspection not found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        It may have been deleted, or the link points to another tenant.
      </Typography>
      <Button
        component={RouterLink}
        to="/data-acquisitions"
        variant="contained"
      >
        Back to data acquisitions
      </Button>
    </Paper>
  );
}

function ErrorCard({
  errorId,
  onRetry,
}: {
  errorId: string;
  onRetry: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      aria-label="Error"
      sx={{ p: 3 }}
    >
      <Stack spacing={1}>
        <Typography variant="h6" component="h2">
          Couldn&apos;t load inspection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Something went wrong fetching this inspection. Try again.
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace" }}
          data-testid="error-id"
        >
          errorId: {errorId}
        </Typography>
        <Box>
          <Button variant="contained" onClick={onRetry}>
            Retry
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

export function AnalysisFeature() {
  const { inspectionId = "" } = useParams<{ inspectionId: string }>();
  // ANA-R1-05 — dataset-open cache freshening. Fires on mount + DA change so a
  // dataset reopened after a background AI-findings re-import never reconciles
  // against stale (soft-deleted) finding ids.
  useInvalidateFindingsOnOpen(inspectionId);
  const detailQuery = useDataAcquisitionDetailQuery(inspectionId);
  // R58-L-13 — hydrate the viewer toggle state from `?aiToggle` +
  // `?defectToggle` on first mount and mirror back to URL on every flip.
  useBBoxTogglesUrlSync();

  // SB6-ANA-POP-01 — is THIS render a popped-out window? When the URL
  // carries `?popout=1&widget=…` we render only the requested widget (via
  // PopoutHost) instead of the full dashboard. `useViewerPopoutSync` keeps
  // the popped Viewer's active image + selection in lockstep with the main
  // window over the existing BroadcastChannel.
  const [searchParams] = useSearchParams();
  const popoutParams = readPopoutParams(searchParams);
  // SB6-ANA-POP-02 — a pop-out window (`?popout=1&windowId=…`) renders only
  // its window's grid (PopoutHost); chrome is suppressed. Both syncs run in
  // every window: image/selection/prefs (viewer) + which-widgets-in-which-
  // window (workspace).
  const isPopout = popoutParams !== null;
  useViewerPopoutSync({ inspectionId, isPopout });
  useWorkspaceSync({ inspectionId, isPopout });
  // SB6-ANA-POP-04 — cross-window filter sync: the global selection
  // (incl. defectTypeIds) + the AI widget's committed aiClassFilter cross all
  // windows, so a filter popped to a third screen re-filters the main window.
  useFilterSync({ inspectionId, isPopout });

  const detail = detailQuery.data;
  const siteIds = detail?.siteId ? [detail.siteId] : [];
  const assetIds = detail?.assetId ? [detail.assetId] : [];
  const sitesQuery = useSitesByIds(siteIds);
  const assetsQuery = useAssetsByIds(assetIds);
  const sitesById = indexById(sitesQuery.data?.items);
  const assetsById = indexById(assetsQuery.data?.items);
  // Story 031 — selection lives in the shared store so both findings AND
  // defects (clicked via DefectsPanel) can highlight in the Viewer.
  const activeBboxId = useSelectionStore((s) => s.activeFindingId);
  const setActiveBbox = useSelectionStore((s) => s.setActive);
  const selectedFindingCount = useSelectionStore(
    (s) => s.selectedFindingIds.size,
  );
  const resetSelection = useSelectionStore((s) => s.reset);

  // ANA-R3-ESC-01 — bottom of the escape stack: when a finding/defect is
  // selected (single active highlight or a multi-select set) and nothing
  // higher (split-view / marquee / draw) is active, ESC deselects
  // everything in the viewer. This is the only keyboard path to a clean
  // "nothing selected" state.
  useEscapeLayer({
    id: "analysis:deselect",
    priority: ESCAPE_PRIORITY.deselect,
    isActive: activeBboxId !== null || selectedFindingCount > 0,
    onEscape: resetSelection,
  });

  // ANA-R3-ESC-01 — marquee passthrough. A right-drag zoom box is owned by
  // the shared Viewer primitive (packages/ui), which cancels it on ESC via
  // its own internal listener and exposes `data-marquee-active` on its
  // wrapper. We register a passthrough layer above draw/deselect so that an
  // ESC mid-drag cancels ONLY the marquee — without this, the same press
  // would also deselect the active bbox. The layer never handles the event
  // itself; it just blocks the lower layers and lets the primitive act.
  useEffect(() => {
    return registerEscapeLayer({
      id: "analysis:marquee",
      priority: ESCAPE_PRIORITY.marquee,
      isActive: () =>
        typeof document !== "undefined" &&
        document.querySelector('[data-marquee-active="true"]') !== null,
      handle: () => {
        /* owned by the Viewer primitive — passthrough */
      },
      passthrough: true,
    });
  }, []);

  // Story 012 — per-image surface. The Filmstrip publishes activeImageId;
  // the Viewer reads it. The deprecated singular getInspectionImage call
  // is gone (the @aeriosiq/types InspectionImage type stays exported with a
  // @deprecated tag for IDE quick-info; no caller remains).
  const imagesQuery = useImagesQuery(detail?.id ?? "");
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const items = useMemo(
    () => imagesQuery.data?.items ?? [],
    [imagesQuery.data?.items],
  );
  const activeImage: ImageRef | null = useMemo(() => {
    if (!activeImageId) return null;
    return items.find((it) => it.imageId === activeImageId) ?? null;
  }, [activeImageId, items]);

  // ANA-R1-02 — the dataset-wide effective finding set (no image scope). Feeds
  // the cross-widget selection reconciler with the authoritative "which
  // findings exist" set, narrowed only by the shared AI-class filter.
  const datasetFindingSet = useEffectiveFindingSet(detail?.id ?? "");
  const allFindingsQuery = datasetFindingSet.query;
  // SB6-ANA-FILT-07 (AC7) — reconcile the cross-widget selection against the
  // server-filtered findings set. When a threshold change drops the active /
  // multi-selected AI finding, deselect it everywhere. The visible set is the
  // dataset-wide passing findings (same query that feeds the viewer overlay,
  // server-narrowed by the same `aiClassFilter`). `ready` gates on a settled,
  // non-fetching query so a debounce/refetch window never deselects early.
  const allFindingIds = datasetFindingSet.findingIds;
  useFindingSelectionReconciler({
    visibleFindingIds: allFindingIds,
    ready:
      !allFindingsQuery.isPending &&
      !allFindingsQuery.isFetching &&
      allFindingsQuery.data !== undefined &&
      // COMPLETENESS guard: the reconciler must only act on the FULL passing
      // set. The overlay query is capped at pageSize 200; on a DA with more
      // passing findings than that, the page-1 id set is partial and pruning
      // against it would wrongly deselect still-passing findings beyond the
      // cap (AIF-imported datasets can exceed it). Incomplete ⇒ hold off —
      // a stale selection is strictly better than a wrong deselect.
      allFindingsQuery.data.items.length >= allFindingsQuery.data.total,
  });
  // BUGFIX (2026-06-05, Christian's live review): the viewer overlay used to
  // draw from the DATASET-WIDE queries above/below — page 1 only (200 items).
  // On a 4,278-finding DA most images' findings sit beyond page 1, so
  // browsing the filmstrip showed NO bounding boxes. The overlay now uses
  // PER-IMAGE queries (max findings on any image is well under the 200 cap),
  // keyed on the active image so navigation refetches exactly one page.
  // ANA-R1-02 — the per-image overlay reduces from the SAME shared selector as
  // the Tiles widget (`useEffectiveFindingSet`), so the Viewer's boxes and the
  // Tiles' tiles come from byte-identical narrowing (AC1/AC3).
  const activeOverlayImageId = activeImage?.imageId ?? "";
  const overlayFindingSet = useEffectiveFindingSet(detail?.id ?? "", {
    imageId: activeOverlayImageId || null,
  });
  const overlayFindingsQuery = overlayFindingSet.query;
  // ANA-R1-04 — resolve each finding's raw class name to the model display name
  // so the Viewer bbox labels match the List + AI filter module (the model's
  // display name is canonical everywhere user-facing). Unresolvable classes
  // keep the raw name — the box is never dropped.
  const classNames = useClassDisplayNames(detail?.id ?? "");
  // Defects with `bbox` (lyse 1.7.0, server-derived from defect_path)
  // get the same overlay treatment so the user sees the confirmed
  // outline on top of the live full-resolution image — not just on the
  // tiles. PERF-06 — params built by the SHARED helper so the overlay
  // prefetch warms exactly this key.
  const overlayDefectsQuery = useDefectsQuery(
    detail?.id ?? "",
    imageScopedDefectsParams(activeOverlayImageId || null),
  );
  const imageW = activeImage?.width ?? 0;
  const imageH = activeImage?.height ?? 0;
  // R58-L-13 — viewer overlay toggles. Default both on; URL hydrates.
  const aiVisible = useBBoxToggles((s) => s.aiVisible);
  const defectVisible = useBBoxToggles((s) => s.defectVisible);
  // SETTINGS-02 — per-user bounding-box appearance prefs.
  const { prefs } = useLysePreferencesQuery();
  // SETTINGS-02 — bbox appearance is now user-configurable per kind
  // (Bounding Boxes settings tab). Resolve the per-kind style here and
  // attach it to each box; the Viewer honours stroke/fill/outline/label
  // and falls back to legacy constants for omitted fields.
  const bbPrefs = prefs.boundingBox;
  const aiStyle = toBoxStyle(bbPrefs.ai);
  const defectStyle = toBoxStyle(bbPrefs.defect);
  const defectColorBySeverity = bbPrefs.defect.colorBy === "severity";
  // ANA-R3-GEOM-03 — render a finding's TRUE outline when its `path` (the
  // GEOM-01 normalised read) is present; otherwise fall back to the derived
  // `bbox` <Rect> (AC4). A finding lands in EXACTLY ONE of the two lists.
  const findingPolys: ViewerPolygon[] = (
    overlayFindingsQuery.data?.items ?? []
  ).flatMap((f) => {
    if (f.imageId !== activeImage?.imageId) return [];
    const path = f.path;
    if (!path || path.length < 4) return [];
    return [
      {
        id: f.id,
        path,
        color: bbPrefs.ai.color,
        label: classNames.resolve(f.aiClassName).display,
        style: aiStyle,
      },
    ];
  });
  const findingBoxes: BoundingBox[] = (
    overlayFindingsQuery.data?.items ?? []
  ).flatMap((f) => {
    const bb = f.bbox;
    if (!bb || imageW === 0 || imageH === 0) return [];
    if (f.imageId !== activeImage?.imageId) return [];
    if (f.path && f.path.length >= 4) return []; // rendered as a polygon
    return [
      {
        id: f.id,
        x: bb.x * imageW,
        y: bb.y * imageH,
        width: bb.w * imageW,
        height: bb.h * imageH,
        color: bbPrefs.ai.color,
        label: classNames.resolve(f.aiClassName).display,
        style: aiStyle,
      },
    ];
  });
  // OQ-SB6-C3 — defect colour resolution (severity palette vs configured
  // colour), shared by the polygon + bbox builders below.
  const defectColorOf = (d: {
    classColor?: string | null;
    classCategory?: number | null;
  }): string => {
    const sevColor =
      d.classColor ??
      (d.classCategory != null
        ? SEVERITY_BBOX_FALLBACK[d.classCategory]
        : null) ??
      bbPrefs.defect.color;
    return defectColorBySeverity ? sevColor : bbPrefs.defect.color;
  };
  // ANA-R3-GEOM-03 — render a defect's TRUE outline when its `path` is present;
  // otherwise fall back to the derived `bbox` <Rect> (AC4).
  const defectPolys: ViewerPolygon[] = (
    overlayDefectsQuery.data?.items ?? []
  ).flatMap((d) => {
    if (d.imageId !== activeImage?.imageId) return [];
    const path = d.path;
    if (!path || path.length < 4) return [];
    const color = defectColorOf(d);
    const style: BoundingBoxStyle = defectColorBySeverity
      ? { ...defectStyle, color }
      : defectStyle;
    return [{ id: d.id, path, color, label: d.catalogueLabel, style }];
  });
  const defectBoxes: BoundingBox[] = (
    overlayDefectsQuery.data?.items ?? []
  ).flatMap((d) => {
    const bb = d.bbox;
    if (!bb || imageW === 0 || imageH === 0) return [];
    if (d.imageId !== activeImage?.imageId) return [];
    if (d.path && d.path.length >= 4) return []; // rendered as a polygon
    const color = defectColorOf(d);
    const style: BoundingBoxStyle = defectColorBySeverity
      ? { ...defectStyle, color }
      : defectStyle;
    return [
      {
        id: d.id,
        x: bb.x * imageW,
        y: bb.y * imageH,
        width: bb.w * imageW,
        height: bb.h * imageH,
        color,
        label: d.catalogueLabel,
        style,
      },
    ];
  });
  // R58-L-13 AC4 — selection-overrides-toggle.
  // When a toggle is OFF, drop those overlays UNLESS the currently
  // selected bbox is in that kind. The selected bbox is rendered as the
  // ephemeral selection overlay regardless of the global toggle.
  const visibleFindingBoxes = aiVisible
    ? findingBoxes
    : findingBoxes.filter((b) => b.id === activeBboxId);
  const visibleDefectBoxes = defectVisible
    ? defectBoxes
    : defectBoxes.filter((b) => b.id === activeBboxId);
  const overlayBoxes: BoundingBox[] = [
    ...visibleDefectBoxes,
    ...visibleFindingBoxes,
  ];
  // ANA-R3-GEOM-03 — polygon overlays follow the SAME visibility + selection
  // rules as the bbox overlays (a hidden kind still shows its selected shape).
  const visibleFindingPolys = aiVisible
    ? findingPolys
    : findingPolys.filter((p) => p.id === activeBboxId);
  const visibleDefectPolys = defectVisible
    ? defectPolys
    : defectPolys.filter((p) => p.id === activeBboxId);
  const overlayPolygons: ViewerPolygon[] = [
    ...visibleDefectPolys,
    ...visibleFindingPolys,
  ];

  // ANA-R3-GEOM-04 — the defect currently being reshaped in place. Its persisted
  // outline is HIDDEN from the read overlays (the EditGeometryOverlay paints the
  // live draft instead), so a stray drag can't show two shapes (AC2). Also
  // resolve the selected defect's editable geometry so the floating toolbar can
  // seed the editor + position itself by the selection.
  const editingDefectId = useEditGeometryStore((s) => s.defectId);
  const editingDataPolygons = editingDefectId
    ? overlayPolygons.filter((p) => p.id !== editingDefectId)
    : overlayPolygons;
  const editingDataBoxes = editingDefectId
    ? overlayBoxes.filter((b) => b.id !== editingDefectId)
    : overlayBoxes;
  const selectedDefectForEdit: EditableDefect | null = (() => {
    if (activeBboxId === null) return null;
    const d = (overlayDefectsQuery.data?.items ?? []).find(
      (row) => row.id === activeBboxId && row.imageId === activeImage?.imageId,
    );
    if (!d) return null;
    return {
      id: d.id,
      imageId: d.imageId,
      path: d.path ?? null,
      bbox: d.bbox ?? null,
    };
  })();

  if (detailQuery.isPending) {
    return (
      <Stack spacing={1.25}>
        <AnalysisHeaderSkeleton />
      </Stack>
    );
  }

  if (detailQuery.isError) {
    const status = (detailQuery.error as { status?: number } | undefined)
      ?.status;
    if (status === 404) {
      return (
        <Stack spacing={1.25}>
          {/* Workspace mode hides the page-level h1 on the loaded view
              (real-estate budget), but the not-found screen has no
              AnalysisHeader to substitute for it — restore the h1 here
              so the page still has a labelled landmark + matches the
              app-shell smoke spec's heading assertion. */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              position: "absolute",
              width: 1,
              height: 1,
              p: 0,
              m: -1,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            Analysis
          </Typography>
          <NotFoundPanel />
        </Stack>
      );
    }
    const errorId =
      (detailQuery.error as { errorId?: string } | undefined)?.errorId ??
      "unknown";
    return (
      <Stack spacing={1.25}>
        <ErrorCard
          errorId={errorId}
          onRetry={() => {
            void detailQuery.refetch();
          }}
        />
      </Stack>
    );
  }

  if (!detail) return null;

  const surface = (
    // Workspace mode (Christian's brief 2026-05-09 §"Hide chrome"): no
    // page-level h1 anymore — the breadcrumb in `<AppChrome />` already
    // tells the user where they are, and an h1 saying "Analysis" was
    // eating ~64 px of vertical real-estate the Viewer wants. The
    // accessible page heading falls onto the AnalysisHeader's h2.
    <Stack
      spacing={1.25}
      sx={{ minHeight: 0, flex: 1 }}
      {...(isPopout ? { "data-popout-window": "true" } : {})}
    >
      {/* SB6-ANA-POP-01 — in a popped-out window the surrounding dashboard
          chrome (header, filter bar, settings, split-view, image-review) is
          suppressed; only the Viewer (via AnalysisLayoutMount → PopoutHost)
          renders. The data bootstraps + lock model stay so the Viewer's
          overlays + read-only banner behave exactly as in the main window. */}
      {!isPopout && (
        <AnalysisHeaderWithAuth
          detail={detail}
          site={detail.siteId ? sitesById.get(detail.siteId) : undefined}
          asset={detail.assetId ? assetsById.get(detail.assetId) : undefined}
        />
      )}
      {/* SB6-ANA-PROG-01 / SB6-ANA-PROG-03 — dataset-open readiness. The
          mount derives the phase from the queries the surface already issues
          (no new requests) and writes the shared readiness store; it also
          mounts the bottom-fixed status OVERLAY (the consolidated PROG-03
          surface) in the main window. The BRIDGE runs in BOTH the main
          window and pop-outs so the grading gate is enforced everywhere; the
          OVERLAY renders in the main window only (`showOverlay`). */}
      <ReadinessMount detail={detail} showOverlay={!isPopout} />
      {/* Story 048 — single-writer + multi-reader edit lock. The effect
          runs the acquire/heartbeat/release cycle for the writer or the
          poll cycle for readers; the banner surfaces the holder state. */}
      <LockMount detail={detail} />
      {/* Story 033 — hydrate the defect-catalogue store + render the
          "Using global default catalogue" banner (AC6). Catalogue popover
          (Story 020) + Defect drawer (Story 023) consume the store. */}
      <CataloguesBootstrap />
      {/* Story 034 — hydrate per-user Lyse preferences from auth_db. The
          Settings drawer (Story 036) and the image-state filter (042) are
          downstream consumers. */}
      <PreferencesBootstrap />
      {/* Story 042 — image-state filter bridge for the acceptance spec
          (drives applyTo() + setStates()). */}
      <ImageStateFilterBridgeMount />
      {/* SB6-ANA-CACHE-03 — image-cache test bridge (window.__aeroCacheBridge).
          Acceptance-only imperative surface for the LRU/storage scenarios; no
          production code reads it. Main window only. */}
      {!isPopout && <CacheTestBridgeMount />}
      {/* ANA-R1-13 — dataset-open "Download all images now?" prompt. Mounted in
          the main window only (inside PrefetchProvider, so it reads the live
          cache progress + reuses the burger's Download-dataset action). It
          self-gates on the PROG-01 readiness signal so it never delays first
          paint, and on the per-user + per-data-set "don't ask again"
          suppression. NEVER auto-downloads. */}
      {!isPopout && <DatasetDownloadPrompt dataAcquisitionId={detail.id} />}
      {/* Story 036 — Settings drawer (sibling of the workspace so the
          drawer's focus trap doesn't fight the workspace grid). */}
      {!isPopout && (
        <SettingsModal
          // DI-3-S61 — Settings → History reads THIS page's data set.
          history={{
            dataAcquisitionId: detail.id,
            turbine: formatAsset(
              detail.assetId ? assetsById.get(detail.assetId) : undefined,
              detail.assetId,
            ),
            park: formatSite(
              detail.siteId ? sitesById.get(detail.siteId) : undefined,
              detail.siteId,
            ),
            flightDate: detail.flightDate ?? null,
          }}
        />
      )}
      {/* Story 063 + SB6-ANA-POP-01 — pop-out infra: hydrate the per-user
          registry from sessionStorage, expose `window.__aeroLysePopout` for
          e2e, and (parent side) listen for children `closing` to restore
          the in-page slot. */}
      <PopoutInfraMount enabled inspectionId={detail.id} />
      {!isPopout && (
        <FilterWidget
          inspectionId={detail.id}
          activeClassModelId={detail.activeClassModelId ?? null}
        />
      )}
      {/* SB6-ANA-FILT-07 — test-only DOM seam for the AI bbox overlay set.
          The boxes themselves render to a Konva <Stage> canvas (not
          DOM-queryable), so we mirror the visible AI finding-box ids here as
          hidden data so the acceptance spec can assert the viewer narrows
          (AC3). One node per box; `aria-hidden` + visually-hidden keeps it
          out of the a11y tree and the visual surface. */}
      <Box
        aria-hidden="true"
        data-testid="viewer-ai-bbox-ids"
        sx={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
      >
        {visibleFindingBoxes.map((b) => (
          <span key={b.id} data-viewer-ai-bbox-id={b.id} />
        ))}
      </Box>
      <AnalysisLayoutMount
        detail={detail}
        activeImage={activeImage}
        overlayBoxes={editingDataBoxes}
        overlayPolygons={editingDataPolygons}
        selectedDefectForEdit={selectedDefectForEdit}
        autoHideLabelsWhenDense={bbPrefs.autoHideLabelsWhenDense}
        selectedFindingId={activeBboxId}
        onBoxClick={(id) => {
          // R58-L-13 AC7 — resolve which kind the clicked bbox belongs to
          // (defect vs finding) so the cross-widget effect routes the
          // List highlight to the right tab + scrolls the right row into
          // view. We lookup against the source arrays (cheap; one image's
          // worth of overlays is on the order of ten items).
          const isDefect = defectBoxes.some((d) => d.id === id);
          setActiveBbox(id, isDefect ? "defect" : "ai");
        }}
        onFindingSelect={(f) => {
          setActiveBbox(f?.id ?? null, f ? "ai" : null);
        }}
        evaluationState={detail.state}
        prescreenerUserId={detail.prescreenerUserId ?? null}
        expertUserId={detail.expertUserId ?? null}
        activeClassModelId={detail.activeClassModelId ?? null}
        popout={popoutParams}
      />
      {!isPopout && <SplitViewOverlayMount detail={detail} />}
      {!isPopout && <DeleteWithCascadeBridgeMount detail={detail} />}
      {/* R58-L-02 — record per-user image-visit rows (OQ-7). Only fires
          while the DA is in a review phase; debounced 300 ms so a fast
          arrow-key burst collapses to one POST per distinct image. The
          pop-out window does NOT record visits — the main window owns that. */}
      {!isPopout && (
        <ImageReviewStateMount
          activeImageId={activeImage?.imageId ?? null}
          dataAcquisitionId={detail.id}
          evaluationState={detail.state}
        />
      )}
    </Stack>
  );

  // SB6-ANA-CACHE-02 — mount the window-prefetcher in the MAIN window only
  // (a pop-out shares the origin-wide image cache and lets the main window
  // drive the warm). The provider runs the automatic window prefetch and
  // shares the "Download dataset" controls with the dataset-header button.
  // SB6-ANA-CACHE-03 — a human label for the storage UI / Data Acquisitions
  // cached-% rows: "<asset> · <flight code>" (falls back gracefully).
  const datasetName = (() => {
    const codeLabel = String(detail.flightCode ?? detail.instructionCode);
    const assetLabel = detail.assetId
      ? formatAsset(assetsById.get(detail.assetId), detail.assetId)
      : null;
    return assetLabel ? `${assetLabel} · ${codeLabel}` : codeLabel;
  })();

  return isPopout ? (
    surface
  ) : (
    <PrefetchProvider dataAcquisitionId={detail.id} datasetName={datasetName}>
      {surface}
    </PrefetchProvider>
  );
}

/**
 * R58-L-02 — thin wrapper that scopes `useImageReviewState` to the
 * review phases. We enable the hook only while the DA is in
 * `PRE_SCREENING` or `EXPERT_ANALYSIS`. Other phases short-circuit
 * the upsert (the BE returns 403 on non-review phases — this avoids
 * the round-trip + a noisy error toast).
 */
function ImageReviewStateMount({
  activeImageId,
  dataAcquisitionId,
  evaluationState,
}: {
  activeImageId: string | null;
  dataAcquisitionId: string;
  evaluationState:
    | "CREATED"
    | "PROCESSING"
    | "PRE_SCREENING"
    | "EXPERT_ANALYSIS"
    | "EVALUATION_DONE"
    | "LOCKED"
    | "ISSUE"
    | "REJECTED"
    | null;
}): React.ReactElement {
  const enabled =
    evaluationState === "PRE_SCREENING" ||
    evaluationState === "EXPERT_ANALYSIS";
  useImageReviewState({
    activeImageId,
    dataAcquisitionId,
    enabled,
  });
  return <></>;
}

function DeleteWithCascadeBridgeMount({
  detail,
}: {
  detail: DataAcquisitionDetailType;
}): React.ReactElement {
  useDeleteWithCascadeBridge({ dataAcquisitionId: detail.id });
  return <></>;
}

/**
 * Story 042 — mounts the image-state filter store test-bridge under
 * `window.__aeroImageStateFilter`. The acceptance spec asserts
 * `applyTo()` and drives `setStates(...)` directly; no production
 * caller reads the bridge.
 */
function ImageStateFilterBridgeMount(): React.ReactElement {
  useEffect(() => mountImageStateFilterBridge(), []);
  return <></>;
}

/**
 * SB6-ANA-CACHE-03 — mounts the image-cache test bridge under
 * `window.__aeroCacheBridge`. The acceptance spec drives the LRU/storage
 * scenarios (seed datasets, tiny budget, evict, pin, clear) through it.
 */
function CacheTestBridgeMount(): React.ReactElement {
  useEffect(() => installCacheTestBridge(), []);
  return <></>;
}

/**
 * SB6-ANA-PROG-01 / SB6-ANA-PROG-03 — derives the dataset-open readiness
 * phase + renders the bottom-fixed status OVERLAY.
 *
 * It re-issues the surface's core queries by the SAME query keys (TanStack
 * dedups against the in-flight/cached entries — no extra network), so it has
 * live `isPending`/`isError` handles to feed the readiness bridge, plus a
 * `refetch` for the overlay's Retry (AC4). The data-points query is the
 * filtered-view signal for the `filterApplied`/error phases.
 */
function ReadinessMount({
  detail,
  showOverlay,
}: {
  detail: DataAcquisitionDetailType;
  showOverlay: boolean;
}): React.ReactElement {
  const detailQuery = useDataAcquisitionDetailQuery(detail.id);
  // Prefs swallow fetch errors into factory defaults (never an error state)
  // and never block readiness — settled === !isPending.
  const { isPending: prefsPending } = useLysePreferencesQuery();
  const imagesQuery = useImagesQuery(detail.id);
  const findingsQuery = useFindingsQuery(detail.id, { pageSize: 200 });
  const defectsQuery = useDefectsQuery(detail.id, { pageSize: 200 });
  const selection = useDataPointFilterStore((s) => s.selection);
  const dataPointsQuery = useDataPointsQuery(detail.id, selection);

  // `items` is non-nullable on the type, but a settled query with a malformed
  // payload (e.g. a thin test mock) may omit it — read defensively.
  const imageItems = (imagesQuery.data as { items?: unknown[] } | undefined)
    ?.items;
  const datasetEmpty =
    !imagesQuery.isPending &&
    !imagesQuery.isError &&
    (imageItems?.length ?? 0) === 0;

  const toQueryLike = (q: {
    isPending: boolean;
    isError: boolean;
  }): QueryLike => ({ isPending: q.isPending, isError: q.isError });

  useAnalysisReadinessBridge({
    inspectionId: detail.id,
    detail: toQueryLike(detailQuery),
    prefs: { isPending: prefsPending, isError: false },
    images: toQueryLike(imagesQuery),
    findings: toQueryLike(findingsQuery),
    defects: toQueryLike(defectsQuery),
    dataPoints: toQueryLike(dataPointsQuery),
    datasetEmpty,
  });

  const handleRetry = (step: ReadinessStep): void => {
    switch (step) {
      case "detail":
        void detailQuery.refetch();
        return;
      case "images":
        void imagesQuery.refetch();
        return;
      case "findings":
        void findingsQuery.refetch();
        return;
      case "defects":
        void defectsQuery.refetch();
        return;
      case "dataPoints":
        void dataPointsQuery.refetch();
        return;
      case "prefs":
        // Prefs never reach the error phase (errors fold to defaults);
        // refetch all as a safe fallback.
        void detailQuery.refetch();
        return;
    }
  };

  if (!showOverlay) return <></>;
  return <AnalysisStatusbar inspectionId={detail.id} onRetry={handleRetry} />;
}

/**
 * Story 048 — Edit-lock orchestration mount.
 *
 * Reads the D-5 writer-eligibility predicate, runs the lock effect
 * (acquire/heartbeat/release for writers; poll for readers), and
 * renders the banner. The actual editor enable/disable wiring lives
 * downstream in WorkflowHeaderActions / DefectsPanel / etc. — those
 * components already consult `editPermission`. The lock here ensures
 * that even within an eligible-writer phase, only ONE window is the
 * active editor (peer windows for the same user become readers).
 */
function LockMount({
  detail,
}: {
  detail: DataAcquisitionDetailType;
}): React.ReactElement {
  const navigate = useNavigate();
  const me = useMe();
  const editPermission = resolveDefectEditPermission({
    evaluationState: detail.state,
    prescreenerUserId: detail.prescreenerUserId ?? null,
    expertUserId: detail.expertUserId ?? null,
    meId: me.data?.id ?? null,
  });
  const phase: "PRE_SCREENING" | "EXPERT_ANALYSIS" | null =
    detail.state === "PRE_SCREENING"
      ? "PRE_SCREENING"
      : detail.state === "EXPERT_ANALYSIS"
        ? "EXPERT_ANALYSIS"
        : null;
  const lockControls = useLockEffect({
    dataAcquisitionId: detail.id,
    editPermission,
    phase,
  });

  // DEFCAT-R2-02 — lock state drives the contention dialog (foreign
  // holder). The explicit self-release moved into the Asset Info burger's
  // "Release" item (SB6-ANA-HEADER-02), so we publish the lock-release
  // control into the workspace UI store for the burger to drive. The
  // implicit auto-release on unmount/idle is unchanged (it lives in
  // useLockEffect's cleanup).
  const lockState = useLockStore((s) => s.state);
  const registerReleaseLock = useWorkspaceUiStore((s) => s.registerReleaseLock);
  const releaseLock = lockControls.release;
  useEffect(() => {
    registerReleaseLock(releaseLock);
    return () => {
      registerReleaseLock(null);
    };
  }, [registerReleaseLock, releaseLock]);
  // "View" dismisses the dialog for THIS contention without re-opening
  // it until the next contention transition; the page stays read-only.
  const [viewedContention, setViewedContention] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);

  const contended =
    lockState.kind === "contended" && lockState.dataAcquisitionId === detail.id
      ? lockState
      : null;
  const dialogOpen = contended !== null && viewedContention !== detail.id;

  const handleClaim = (reason: string): void => {
    setClaiming(true);
    setClaimError(null);
    void lockControls
      .takeover(reason)
      .then(() => {
        // 200 — holder displaced; the store is now `holding` and the
        // dialog unmounts (contended === null). Editing proceeds exactly
        // as a normal claim.
        setViewedContention(null);
      })
      .catch((err: unknown) => {
        const status = (err as { status?: number } | undefined)?.status;
        if (status === 400) {
          setClaimError("Reason must be at least 10 characters.");
        } else if (status === 409) {
          setClaimError("This data set is still locked. Try again.");
        } else {
          setClaimError("Couldn't take over the lock. Try again.");
        }
      })
      .finally(() => {
        setClaiming(false);
      });
  };

  return (
    <>
      <ReadOnlyCollaboratorBanner
        dataAcquisitionId={detail.id}
        isWriter={editPermission.allowed && phase !== null}
        evaluationState={detail.state}
      />
      {/* SB6-ANA-HEADER-02 — the standalone lock-release row is gone; the
          explicit release is absorbed by the burger's "Release"
          (self-unassign), which calls the same lockControls.release()
          published into the workspace UI store above. */}
      {contended ? (
        <LockContentionDialog
          open={dialogOpen}
          holderName={contended.heldBy.displayName}
          heldSince={contended.heldSince}
          claiming={claiming}
          claimError={claimError}
          onView={() => {
            // Open read-only: dismiss the dialog, keep the contended /
            // read-only state. No lock is acquired.
            setViewedContention(detail.id);
          }}
          onClaim={handleClaim}
          onCancel={() => {
            // Leave the surface entirely — back to the list.
            void navigate("/data-acquisitions");
          }}
        />
      ) : null}
    </>
  );
}

function SplitViewOverlayMount({
  detail,
}: {
  detail: DataAcquisitionDetailType;
}): React.ReactElement {
  const me = useMe();
  const editPermission: DefectEditPermission = resolveDefectEditPermission({
    evaluationState: detail.state,
    prescreenerUserId: detail.prescreenerUserId ?? null,
    expertUserId: detail.expertUserId ?? null,
    meId: me.data?.id ?? null,
  });
  return (
    <SplitViewOverlay
      dataAcquisitionId={detail.id}
      editPermission={editPermission}
    />
  );
}

// Thin wrapper — hoists the `useMe()` call out of AnalysisHeader so the
// header itself stays pure (the existing Story 008 acceptance test
// expects to render <AnalysisHeader/> without an auth provider).
function AnalysisHeaderWithAuth({
  detail,
  site,
  asset,
}: {
  detail: DataAcquisitionDetailType;
  site: SiteSummary | undefined;
  asset: AssetSummary | undefined;
}): React.ReactElement {
  const me = useMe();
  return (
    <AnalysisHeader
      detail={detail}
      site={site}
      asset={asset}
      prescreenerUserId={detail.prescreenerUserId ?? null}
      expertUserId={detail.expertUserId ?? null}
      meId={me.data?.id ?? null}
      meRoles={me.data?.roles}
    />
  );
}

// ANA-R4-LAYOUT-01 — the FilterWidgetWithVerify wrapper + VerifyAllRow were
// removed: the "Verify all" button consumed canvas height and cut off the
// Viewer/Tiles on a 4K screen. The FilterWidget is now rendered directly at the
// call site. The BE bulk-verify endpoint stays; only the FE surface is gone.
// (Defect state model unresolved — tracked in .vibe/31, intentionally untouched.)

interface AnalysisLayoutMountProps {
  detail: { id: string; instructionCode: number };
  activeImage: ImageRef | null;
  overlayBoxes: BoundingBox[];
  // ANA-R3-GEOM-03 — free-form defect/finding outlines (true polygons).
  overlayPolygons: ViewerPolygon[];
  // ANA-R3-GEOM-04 — the selected defect's editable geometry (or null), so the
  // viewer cell can mount the in-place geometry editor + floating toolbar.
  selectedDefectForEdit: EditableDefect | null;
  // SETTINGS-02 — zoom-aware label declutter (from bbox prefs).
  autoHideLabelsWhenDense: boolean;
  selectedFindingId: string | null;
  onBoxClick: (id: string) => void;
  onFindingSelect: (f: AiFinding | null) => void;
  // D-5 inputs (story 008 AC7–AC10). Hand-lifted from `detail` at the
  // call site so `AnalysisLayoutMount` doesn't need to know about the
  // generated-type extension shim.
  evaluationState:
    | "CREATED"
    | "PROCESSING"
    | "PRE_SCREENING"
    | "EXPERT_ANALYSIS"
    | "EVALUATION_DONE"
    | "LOCKED"
    | "ISSUE"
    | "REJECTED"
    | null;
  prescreenerUserId: string | null;
  expertUserId: string | null;
  // SB6-ANA-LIST-02 — DA's active class-model id (or "none" / empty
  // string when no model is selected). Threaded into the ListWidget
  // so the Defects table can hide the Severity column entirely.
  activeClassModelId: string | null;
  // SB6-ANA-POP-02 — when set, this is a popped-out window: render the grid
  // of widgets assigned to this window (via PopoutHost), not the dashboard.
  popout?: { windowId: string } | null;
}

// Workspace host — drag/resize/hide RGL grid for the analysis widgets.
// `FilterWidget` lives above this mount in `AnalysisFeature`, not in
// the grid (it's chrome, not a widget).
function AnalysisLayoutMount({
  detail,
  activeImage,
  overlayBoxes,
  overlayPolygons,
  selectedDefectForEdit,
  autoHideLabelsWhenDense,
  selectedFindingId,
  onBoxClick,
  onFindingSelect,
  evaluationState,
  prescreenerUserId,
  expertUserId,
  activeClassModelId,
  popout = null,
}: AnalysisLayoutMountProps): React.ReactElement | null {
  const me = useMe();
  // ANA-R3-GEOM-03 — receive the Viewer's live OSD viewport projection so
  // the sibling DrawOverlay can build its CoordinateTransform.
  const setViewportApi = useViewportApiStore((vs) => vs.setApi);
  // ANA-R3-GEOM-04 — the live PolygonEditor API published by the
  // EditGeometryOverlay, consumed by the floating toolbar's Save/Undo. State
  // (not a ref) so the toolbar re-renders when `canUndo` flips.
  const [editGeometryApi, setEditGeometryApi] =
    useState<PolygonEditorApi | null>(null);
  // Workspace state is keyed per-user so two analysts on the same
  // browser keep their own preferences. `anonymous` is the pre-login
  // fallback (e.g. while /me is in flight).
  const userId = me.data?.id ?? "anonymous";

  // Story 008 D-5 capability gate. Computed once here and threaded
  // down to ListWidget → DefectsPanel so the panel stays pure.
  const defectEditPermission: DefectEditPermission =
    resolveDefectEditPermission({
      evaluationState,
      prescreenerUserId,
      expertUserId,
      meId: me.data?.id ?? null,
    });

  // SB6-ANA-VIEW-05 follow-up — the toolbar's AI / defect toggle icons take
  // their colour from the user's Settings → Bounding Boxes picks (the same
  // shared, cached LysePreferences query that colours the bbox strokes) so
  // the icons match and recolour live when the user changes them.
  const { prefs } = useLysePreferencesQuery();

  // TRIAGE-02 — undo failures the inspector must know about (e.g. the
  // undismiss role gate until the BE self-undo bypass lands). One slot.
  const [undoErrorSnack, setUndoErrorSnack] = useState<string | null>(null);

  // Story 064 → TRIAGE-02 — install the global Ctrl+Z / Ctrl+Shift+Z
  // undo/redo hotkeys. Inert when the user can't edit (AC11).
  useUndoHotkey({
    dataAcquisitionId: detail.id,
    editAllowed: defectEditPermission.allowed,
    onUndoError: setUndoErrorSnack,
  });

  // TRIAGE-01/02 (audit 3.2 / IMP-01/02/05) — hydrate the per-user triage
  // prefs (configurable keymap + auto-advance) and install the global
  // triage hotkey layer: j/k finding cursor, Space / Shift+Space queue
  // jumps, and the decision keys (a/Enter promote, x/Del instant
  // undoable dismiss, Shift+X reason dialog, 1..5 severity) with
  // auto-advance. Mounted here — like useUndoHotkey — so pop-out windows
  // get their own per-window listener.
  const hydrateTriagePrefs = useTriagePreferencesStore((s) => s.hydrate);
  useEffect(() => {
    hydrateTriagePrefs(userId);
  }, [hydrateTriagePrefs, userId]);
  // Severity story (8.0.0 / UX Q6) — resolve the DA's effective class
  // model so the 1..5 grade keys map RELATIVE to it (and no-op for
  // grades the model doesn't have / under the None model).
  const effectiveClassModel = useEffectiveClassModel(activeClassModelId);
  const triageDecisions = useTriageDecisions({
    dataAcquisitionId: detail.id,
    editAllowed: defectEditPermission.allowed,
    classModel: effectiveClassModel.classModel,
  });
  useTriageHotkeys({
    dataAcquisitionId: detail.id,
    editAllowed: defectEditPermission.allowed,
    decisions: triageDecisions.handlers,
  });

  // Story 024 — focus-equals-review status auto-advance. Mounted once
  // here so every activation pathway (tile click / row click / Sketch
  // marker / Viewer BBox / arrow keys) flows through the same effect.
  const [focusSnack, setFocusSnack] = useState<{ errorId: string } | null>(
    null,
  );
  useFocusEqualsReviewEffect({
    inspectionId: detail.id,
    evaluationState,
    editAllowed: defectEditPermission.allowed,
    onError: () => {
      setFocusSnack({ errorId: "auto-advance-failed" });
    },
  });

  // SB6-ANA-CACHE-01 — resolve the active image's full-res URL through the
  // canonical-key image cache. On a cache hit this yields a local blob: URL
  // (zero network for the bytes, survives SAS rotation + reload); on a miss it
  // returns the SAS URL unchanged AND populates the cache in the background, so
  // the existing progressive thumb900→full upgrade is untouched. Object URLs
  // are revoked when the active image changes / on unmount (no blob: leaks).
  // Scope is full-res only — thumb120/thumb900 stay on the browser HTTP cache.
  const cachedFullUrl = useCachedImageUrl(activeImage?.urls.full ?? null);

  // Story 064 — bridge the active-image-id from imageActivationStore
  // into the per-image undo stack so the stack clears when the user
  // moves to a different image. A separate effect (rather than
  // co-locating with the activation store) keeps the activation store
  // free of analysis-feature concerns.
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const setUndoActiveImage = useUndoStackStore((s) => s.setActiveImage);
  useEffect(() => {
    setUndoActiveImage(activeImageId);
  }, [activeImageId, setUndoActiveImage]);

  // ANA-R3-GEOM-04 — leaving the image abandons any in-progress geometry edit
  // (its draft is image-bound; nothing persisted without Save — AC2).
  const cancelEditGeometry = useEditGeometryStore((s) => s.cancel);
  useEffect(() => {
    cancelEditGeometry();
  }, [activeImageId, cancelEditGeometry]);

  // SB6-ANA-DEF-01 Track 5.2 — resolve the data point matching the active
  // image (1:1 via `data_points.vis_image_id`). The DefectDrawer needs its
  // `componentGroupCode` to scope the new Location-Detail dropdown. We
  // reuse the global filter selection so the cache key matches whatever
  // other widgets have already fetched.
  const dataPointFilterSelection = useDataPointFilterStore((s) => s.selection);
  const dataPointsQuery = useDataPointsQuery(
    detail.id,
    dataPointFilterSelection,
  );
  const activeDataPoint = useMemo(() => {
    if (!activeImageId || !dataPointsQuery.data) return null;
    return (
      dataPointsQuery.data.items.find(
        (dp) => dp.visImageId === activeImageId,
      ) ?? null
    );
  }, [activeImageId, dataPointsQuery.data]);

  // Story 064 — soft-delete wrapper used by the test-bridge (E2E spec
  // AC5). The wrapper pushes a `softDeleteDefect` entry onto the stack
  // and appends an audit-log row.
  const softDelete = useUndoableSoftDeleteDefectMutation(detail.id);
  const defectsQuery = useDefectsQuery(detail.id, { pageSize: 200 });
  useEffect(() => {
    interface Bridge {
      setActiveImage: (id: string | null) => void;
      softDeleteDefect: (defectId: string) => Promise<void>;
    }
    const w = window as unknown as { __aeroUndoBridge?: Bridge };
    const items = defectsQuery.data?.items ?? [];
    w.__aeroUndoBridge = {
      setActiveImage: (id) => {
        useImageActivationStore.getState().setActive(id);
      },
      softDeleteDefect: async (defectId) => {
        const snapshot = items.find((d) => d.id === defectId);
        if (!snapshot) return;
        await softDelete.mutateAsync({
          defectId,
          defectSnapshot: snapshot,
          dataAcquisitionId: detail.id,
          imageId: snapshot.imageId,
        });
      },
    };
    return () => {
      const w2 = window as unknown as { __aeroUndoBridge?: Bridge };
      delete w2.__aeroUndoBridge;
    };
  }, [defectsQuery.data, softDelete, detail.id]);

  // Per-image zoom store (Story 012). The Viewer reads its initial
  // viewport from here on first open and writes back on every change.
  const getViewport = useViewerZoomStore((s) => s.getViewport);
  const setViewport = useViewerZoomStore((s) => s.setViewport);
  const initialViewport = activeImage
    ? (getViewport(activeImage.imageId) ?? null)
    : null;
  const handleViewportChange = (state: {
    zoom: number;
    centerX: number;
    centerY: number;
  }): void => {
    if (!activeImage) return;
    setViewport(activeImage.imageId, state);
  };

  // ANA-R3-NAV-01 — arrow keys in the Viewer step the active data point
  // (Up/Left → previous, Down/Right → next) instead of panning the image,
  // walking the SAME filtered, capture-ordered set as the Filmstrip.
  const { goToPrevious, goToNext } = useDataPointNavigation(detail.id);
  const handleArrowNavigate = useCallback(
    (direction: "previous" | "next") => {
      if (direction === "previous") goToPrevious();
      else goToNext();
    },
    [goToPrevious, goToNext],
  );

  // Sketch's "you are here" cue — radius along the blade in metres,
  // sourced from the active image's `geometry.componentZ` (data_points
  // snippet on the BE side; nullable since 1.6.0 if the data acquisition
  // pre-dates per-image geometry).
  //
  // Fallback (Christian's review 2026-05-10): when componentZ is null OR
  // exactly 0 (legacy data acquisitions where the planned position
  // wasn't backfilled), average the radii of the defects ON the active
  // image instead. Not perfect — the photo may not be exactly at the
  // defects' centroid — but better than pinning the marker to 0 m.
  const layoutDefectsQuery = useDefectsQuery(detail.id, { pageSize: 200 });
  const activeImageDefectRadiusM = useMemo<number | null>(() => {
    if (!activeImage) return null;
    const onImage = (layoutDefectsQuery.data?.items ?? []).filter(
      (d) => d.imageId === activeImage.imageId && d.radiusM != null,
    );
    if (onImage.length === 0) return null;
    const sum = onImage.reduce((acc, d) => acc + Math.abs(d.radiusM ?? 0), 0);
    return sum / onImage.length;
  }, [activeImage, layoutDefectsQuery.data]);
  const componentZ = activeImage?.geometry?.componentZ ?? null;
  const activeImageRadiusM =
    componentZ != null && componentZ !== 0
      ? componentZ
      : activeImageDefectRadiusM;

  // R58-L-07 AC4 — dataset counts for the ListWidget count badge.
  // `layoutDefectsQuery` is already in flight and gives the defect tab count.
  //
  // ANA-R1-03 — the AI-findings tab badge now reflects the CURRENT global
  // data-point filter (incl. L1/L2 status + reviewState) AND the AI
  // class/confidence narrowing, not the dataset-wide `getFindings` total.
  // The old badge was server-computed (`getFindings` `total`, narrowed only
  // by `aiClassFilter` since FILT-07) and so over-counted whenever the global
  // filter scoped the dataset — e.g. it kept counting findings on already-seen
  // images under an unseen-only filter. `useFilteredFindingCount` derives the
  // count client-side from the same two shared selectors the Tiles widget's
  // "All images" scope uses (filtered vis-image ids ∩ effective findings), so
  // the tab badge can never disagree with the list / tiles (AC1–AC3).
  const filteredFindingCount = useFilteredFindingCount(detail.id);
  const datasetFindingsCount = filteredFindingCount.count;
  const datasetDefectsCount = layoutDefectsQuery.data?.total;

  // SB6-ANA-VIEW-05 / SB6-ANA-POP-01 — the Viewer cell, extracted so it can
  // render BOTH in the dashboard grid and (unchanged) inside a popped-out
  // window via PopoutHost. The live OSD canvas can't move across windows;
  // the pop-out boots a fresh instance from this same JSX and mirrors the
  // main window's active image/selection over the sync channel.
  const viewerBody = (
    <Box
      sx={{ height: "100%", position: "relative" }}
      data-active-image-id={activeImage?.imageId}
    >
      {activeImage ? (
        <>
          <AnalysisViewerPanel
            // Progressive load: open with thumb_900 (small, already
            // cached), upgrade to full-res in the background — Christian's
            // brief 2026-05-10 §"loading seems take quite some time".
            imageUrl={activeImage.urls.thumb900}
            // SB6-ANA-CACHE-01 — full-res source resolved through the canonical
            // image cache: a blob: URL on a hit (zero network), the raw SAS URL
            // on a miss. `null` while the (sub-ms) cache probe runs — the Viewer
            // simply stays on thumb900 for that tick rather than firing a
            // full-res request a cache hit would make redundant.
            progressiveImageUrl={cachedFullUrl}
            fallbackImageUrl={activeImage.urls.thumb900}
            ariaLabel={`Inspection ${String(detail.instructionCode)} image · ${activeImage.basename}`}
            boundingBoxes={overlayBoxes}
            polygonOverlays={overlayPolygons}
            onViewportApiReady={setViewportApi}
            selectedBoxId={selectedFindingId}
            onBoxClick={onBoxClick}
            initialViewport={initialViewport}
            onViewportChange={handleViewportChange}
            // SB6-ANA-VIEW-03 — Pass the active image; the panel calls
            // useRadiusReadout internally. The readout is now always
            // visible (image-centre stand-in until Story 032's pic2comp
            // ships) — including in LOCKED state per Christian.
            activeImage={activeImage}
            // SETTINGS-02 — zoom-aware label declutter.
            autoHideLabelsWhenDense={autoHideLabelsWhenDense}
            denseLabelNotice="Many bounding boxes — labels hidden automatically"
            // ANA-R3-NAV-01 — arrow keys jump prev/next data point.
            onArrowNavigate={handleArrowNavigate}
          />
          {/* SB6-ANA-VIEW-05 — the three Viewer controls grouped into one
              draggable toolbar, clamped inside this relative Viewer cell.
              SB6-ANA-VIEW-04's lock gate is expressed via `editAllowed`. */}
          <ViewerToolbar
            editAllowed={defectEditPermission.allowed}
            aiColor={prefs.boundingBox.ai.color}
            defectColor={prefs.boundingBox.defect.color}
          />
          <DrawOverlay
            imageId={activeImage.imageId}
            imageWidth={activeImage.width}
            imageHeight={activeImage.height}
          />
          {/* ANA-R3-GEOM-04 — in-place geometry editor for an existing defect.
              The overlay hosts the shared PolygonEditor (only mounted while
              editing); the floating toolbar offers Edit/Save/Cancel/Undo by the
              selection and gates on `editAllowed` (AC5). */}
          <EditGeometryOverlay onEditorApi={setEditGeometryApi} />
          <EditGeometryToolbar
            selectedDefect={selectedDefectForEdit}
            editAllowed={defectEditPermission.allowed}
            inspectionId={detail.id}
            imageWidth={activeImage.width}
            imageHeight={activeImage.height}
            editorApi={editGeometryApi}
          />
        </>
      ) : (
        <NoImagesPanel />
      )}
    </Box>
  );

  // SB6-ANA-POP-02 — the widget bodies, built once so BOTH the main grid and
  // a pop-out window (PopoutHost) can render any of them.
  const widgets: Record<WidgetKey, React.ReactNode> = {
    sketch: (
      <SketchWidget
        inspectionId={detail.id}
        componentId={null}
        activeImageRadiusM={activeImageRadiusM}
      />
    ),
    list: (
      <ListWidget
        inspectionId={detail.id}
        onFindingSelect={onFindingSelect}
        defectEditPermission={defectEditPermission}
        activeClassModelId={activeClassModelId}
        // SB6-ANA-LIST-01 — default tab from DA state (Defects past
        // pre-screening, else AI Findings).
        evaluationState={evaluationState}
        // R58-L-07 AC4 — dataset-wide counts for the `<current> / <dataset>`
        // badge, reusing the queries AnalysisFeature already issues.
        datasetFindingsCount={datasetFindingsCount}
        datasetDefectsCount={datasetDefectsCount}
      />
    ),
    // SB6-ANA-POP-01 — extracted to `viewerBody` so a pop-out window renders
    // the exact same Viewer.
    viewer: viewerBody,
    tiles: (
      <TilesWidget
        dataAcquisitionId={detail.id}
        editAllowed={defectEditPermission.allowed}
      />
    ),
    filmstrip: <FilmstripWidget dataAcquisitionId={detail.id} />,
    // SB6-ANA-FILT-06 (D5) — advanced filter widget shells (Track A).
    // Bodies filled in Tracks B/C; placement is handled by the workspace.
    aiFilter: <AiFilterWidget dataAcquisitionId={detail.id} />,
    defectFilter: <DefectFilterWidget dataAcquisitionId={detail.id} />,
    // Story 056 — image histogram (blown-out detection). Reads the same
    // activation the Filmstrip publishes; bytes come via the image cache.
    histogram: <HistogramWidget dataAcquisitionId={detail.id} />,
  };

  // SB6-ANA-POP-02 — a pop-out window renders the grid of widgets assigned to
  // its windowId (PopoutHost owns the window chrome + lifecycle), not the
  // dashboard. Any widget body comes from the same `widgets` map.
  if (popout) {
    return (
      <>
        <PopoutHost
          inspectionId={detail.id}
          renderWidget={(w) =>
            (widgets as Record<string, React.ReactNode>)[w] ?? null
          }
        />
        {/* TRIAGE-02 — the triage snackbar + Shift+X reason dialog must
            exist per window (hotkeys bind per window). */}
        {triageDecisions.element}
      </>
    );
  }

  return (
    <>
      <AnalysisWorkspace userId={userId} widgets={widgets} />
      {/* Story 023 + SB6-ANA-DEF-01 — annotation drawer. Sibling of the
        workspace so its focus trap doesn't fight the grid. Passes the
        active data point so the drawer can scope the Location-Detail
        dropdown by `componentGroupCode`. */}
      <DefectDrawer
        inspectionId={detail.id}
        activeImage={activeImage}
        activeDataPoint={activeDataPoint}
        activeClassModelId={activeClassModelId}
      />
      {/* TRIAGE-02 — triage decision feedback (promote/dismiss/severity
          snackbar with Undo) + the Shift+X dismiss-reason dialog. */}
      {triageDecisions.element}
      {/* TRIAGE-02 — undo failure surface (e.g. undismiss role gate). */}
      {undoErrorSnack ? (
        <Snackbar
          open
          autoHideDuration={6000}
          onClose={() => {
            setUndoErrorSnack(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => {
              setUndoErrorSnack(null);
            }}
          >
            {undoErrorSnack}
          </Alert>
        </Snackbar>
      ) : null}
      {/* Story 024 — focus-equals-review error toast. Single slot so
          rapid arrow-key navigation can't queue duplicates. */}
      {focusSnack ? (
        <Snackbar
          open
          autoHideDuration={6000}
          onClose={() => {
            setFocusSnack(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => {
              setFocusSnack(null);
            }}
          >
            Could not update status (errorId: {focusSnack.errorId})
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
}

function NoImagesPanel(): React.ReactElement {
  return (
    <Box
      role="status"
      aria-label="No images for this inspection"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1,
        p: 4,
      }}
    >
      <Typography variant="subtitle1">No images for this inspection</Typography>
      <Typography variant="body2" color="text.secondary">
        It may still be uploading from the drone or processing in the BE.
      </Typography>
    </Box>
  );
}
